import { Link } from 'gatsby';

import Icon from 'molecules/Icon';

import { LinkStyles } from 'components/Resources/styles/Pagination.styles';

import { createSlugFromName } from 'utils/createSlugFromName';

import type { FC } from 'react';

type PaginationProps = {
  currentPage: number;
  baseUrl: string;
  totalPages: number;
  hasNextPage: boolean;
  linkState: {
    industry?: string;
    tags?: string[];
  };
  setCurrentPage?: () => void;
};

const Pagination: FC<PaginationProps> = ({
  currentPage,
  baseUrl,
  totalPages,
  hasNextPage,
  linkState,
  setCurrentPage,
}) => {
  const formattedBaseUrl = createSlugFromName(baseUrl);
  const formattedPrevLink =
    currentPage === 2 ? `${formattedBaseUrl}#listing` : `${formattedBaseUrl}/page/${currentPage - 1}#listing`;
  const formattedNextLink = `${formattedBaseUrl}/page/${currentPage + 1}#listing`;

  const maxPagesToShow = 5;
  const halfMaxPagesToShow = Math.floor(maxPagesToShow / 2);

  let startPage = Math.max(1, currentPage - halfMaxPagesToShow);
  let endPage = Math.min(totalPages, currentPage + halfMaxPagesToShow);

  if (startPage === 1) {
    endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);
  } else if (endPage === totalPages) {
    startPage = Math.max(1, endPage - maxPagesToShow + 1);
  }

  const pageNumbers = Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i);

  const paginationLinks = pageNumbers.map((page, index) => (
    <Link
      key={page}
      className={`flex size-10 items-center justify-center font-semibold text-gray-900 ${
        page === currentPage ? 'bg-gray-900 text-white' : ''
      }`}
      data-cy={`page-${index + 1}`}
      to={page === 1 ? `${formattedBaseUrl}#listing` : `${formattedBaseUrl}/page/${page}#listing`}
      rel={page + 1 === currentPage ? 'prev' : (page - 1 === currentPage && 'next') || undefined}
      state={linkState}
      onClick={() => setCurrentPage(index + 1)}
    >
      {page.toString()}
    </Link>
  ));

  return (
    <div className="relative flex flex-wrap items-center justify-center gap-1 md:gap-2" data-cy="paginationContainer">
      {currentPage !== 1 && (
        <Link
          dataCyAttribute="prevArrow"
          to={formattedPrevLink}
          className={LinkStyles({ disabled: currentPage === 1 })}
          rel="prev"
          state={linkState}
          onClick={() => setCurrentPage(currentPage - 1)}
        >
          <Icon className="fill-white" icon="arrow-left" size={20} />
        </Link>
      )}
      {paginationLinks}
      {hasNextPage && (
        <Link
          dataCyAttribute="nextArrow"
          to={formattedNextLink}
          className={LinkStyles({ disabled: !hasNextPage })}
          rel="next"
          state={linkState}
          onClick={() => setCurrentPage(currentPage + 1)}
        >
          <Icon className="fill-white" icon="arrow-right" size={20} />
        </Link>
      )}
    </div>
  );
};

export default Pagination;
